import { encodeBase64 } from "chatkit-web-shared/frameEncoding"

import { ChatFrameMessenger } from "./ChatFrameMessenger"
import { debounce, removeMethods } from "./helpers"

import type { Attachment, ChatKitFrameParams, ChatKitOptions } from "chatkit-web-shared/types"

const CHATKIT_FRAME_URL = import.meta.env.CHATKIT_FRAME_URL

interface ChatKitElementEventMap {
  "chatkit.error": CustomEvent<{ error: Error }>
  "chatkit.popup.open": CustomEvent<void>
  "chatkit.popup.close": CustomEvent<void>
  "chatkit.response.start": CustomEvent<void>
  "chatkit.response.end": CustomEvent<void>
  "chatkit.thread.change": CustomEvent<{ threadId: string | null }>
  "chatkit.log": CustomEvent<{ name: string; data?: Record<string, unknown> }>
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class ChatKitElement extends HTMLElement {
  #opts?: ChatKitOptions
  #frame?: HTMLIFrameElement
  #wrapper?: HTMLDivElement

  #shadow = this.attachShadow({ mode: "open" })

  #messenger = new ChatFrameMessenger({
    fetch: ((...args) => {
      const customFetch = this.#opts?.fetch ?? fetch
      return customFetch(...args)
    }) as typeof fetch,
    target: () => this.#frame?.contentWindow ?? null,
    targetOrigin: new URL(CHATKIT_FRAME_URL).origin,
    handlers: {
      onFileInputClick: ({ inputAttributes }) => {
        return new Promise<File[]>((resolve) => {
          const input = document.createElement("input")
          for (const [key, value] of Object.entries(inputAttributes)) {
            input.setAttribute(key, String(value))
          }
          const respond = () => {
            resolve(Array.from(input.files || []))
            if (this.#shadow.contains(input)) {
              this.#shadow.removeChild(input)
            }
          }
          input.addEventListener("cancel", respond)
          input.addEventListener("change", respond)
          this.#shadow.appendChild(input)
          input.click()
        })
      },
      onClientToolCall: async ({ name, params }) => {
        const fc = this.#opts?.clientTools?.[name]
        if (!fc) {
          throw new Error(
            `No handler for client tool: "${name}". Please include clientTools.${name} on <chat-kit>.options.`,
          )
        }
        return fc(params)
      },
      onEntitySearch: async ({ query }) => this.#opts?.entities?.search?.(query) ?? [],
      onEntityClick: async ({ entity }) => this.#opts?.entities?.onClick?.(entity),
      onEntityPreview: async ({ entity }) =>
        this.#opts?.entities?.getPreview?.(entity) ?? { preview: null },
    },
  })

  #sendWindowSize = debounce(() => {
    this.#messenger?.emit("outer_window_size", { width: window.innerWidth })
  }, 200)

  #setOptionsDataAttributes(options: ChatKitOptions) {
    if (options.mode === "popup") {
      this.dataset.mode = "popup"
      this.dataset.popupOpen = "false"
    } else {
      this.dataset.mode = "inline"
    }
    this.dataset.colorScheme =
      typeof options.theme === "string" ? options.theme : options.theme?.colorScheme ?? "light"
  }

  #handleFrameLoad = () => {
    this.dataset.loaded = "true"
    this.#sendWindowSize()
    this.dispatchEvent(new CustomEvent("chatkit.ready", { bubbles: true, composed: true }))
  }

  connectedCallback() {
    const style = document.createElement("style")
    style.textContent = `
      :host {
        display: block;
        position: relative;
        height: 100%;
        width: 100%;
      }
      :host([data-mode="popup"][data-popup-open="false"]) {
        display: none;
      }
      :host([data-mode="popup"][data-popup-open="true"]) {
        display: block;
        position: fixed;
        z-index: 99999;
        height: 100vh;
        width: 100vw;
        inset: 0;
      }
      .ck-iframe {
        border: none;
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        color-scheme: light only;
      }
      .ck-wrapper {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        opacity: 0;
      }
      :host([data-color-scheme="dark"]) .ck-iframe {
        color-scheme: dark only;
      }
      :host([data-loaded="true"]) .ck-wrapper {
        opacity: 1;
      }
      :host([data-mode="popup"][data-popup-open="false"]) .ck-wrapper  {
        display: none;
      }
      :host([data-mode="popup"][data-popup-open="true"]) .ck-wrapper {
        display: block;
        position: fixed;
        z-index: 99999;
      }
    `

    const frame = document.createElement("iframe")
    frame.className = "ck-iframe"
    frame.name = "chatkit"
    frame.role = "presentation"
    frame.tabIndex = 0
    frame.setAttribute("allowtransparency", "true")
    frame.setAttribute("frameborder", "0")
    frame.setAttribute("scrolling", "no")
    frame.setAttribute("allow", "clipboard-read; clipboard-write")
    this.#frame = frame

    // not sure we still need this..
    const wrapper = document.createElement("div")
    wrapper.className = "ck-wrapper"
    wrapper.appendChild(frame)
    this.#wrapper = wrapper

    this.#shadow.append(style)

    this.#messenger.on("popup_exit_complete", () => {
      this.dataset.popupOpen = "false"
    })
    this.#messenger.on("left_header_icon_click", () => {
      const header = this.#opts?.header
      if (typeof header !== "boolean" && header?.controls?.customButtonLeft) {
        header.controls.customButtonLeft.onClick()
      }
    })
    this.#messenger.on("right_header_icon_click", () => {
      const header = this.#opts?.header
      if (typeof header !== "boolean" && header?.controls?.customButtonRight) {
        header.controls.customButtonRight.onClick()
      }
    })
    this.#messenger.on("public_event", ([event, data]) => {
      this.dispatchEvent(new CustomEvent(`chatkit.${event}`, { detail: data }))
    })

    frame.addEventListener("load", this.#handleFrameLoad, { once: true })
    window.addEventListener("resize", this.#sendWindowSize)

    this.#maybeInit()
  }

  #initialized = false
  #maybeInit() {
    if (this.#initialized || !this.#frame || !this.#opts) {
      return
    }
    this.#initialized = true
    this.#setOptionsDataAttributes(this.#opts)
    const frameURL = new URL(CHATKIT_FRAME_URL)
    frameURL.hash = encodeBase64({
      options: removeMethods(this.#opts),
      referrer: window.location.origin,
      outerWindowWidth: window.innerWidth,
    } satisfies ChatKitFrameParams)
    this.#messenger.connect()
    this.#frame.src = frameURL.toString()
    // Impossible to not exist
    if (this.#wrapper) {
      this.#shadow.append(this.#wrapper)
    }
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.#sendWindowSize)
    this.#frame?.removeEventListener("load", this.#handleFrameLoad)
    this.#messenger.disconnect()
  }

  setOptions(newOptions: ChatKitOptions) {
    this.#opts = newOptions
    if (this.#initialized) {
      this.#setOptionsDataAttributes(this.#opts)
      this.#messenger.commands.setOptions(removeMethods(newOptions))
    } else {
      this.#maybeInit()
    }
  }

  async open() {
    this.dataset.popupOpen = "true"
    await this.#messenger?.commands.open()
    await this.focusComposer()
  }

  async close() {
    await this.#messenger?.commands.close()
  }

  async focusComposer() {
    this.#frame?.focus()
    await this.#messenger?.commands.focusComposer()
  }

  async fetchUpdates() {
    await this.#messenger?.commands.fetchUpdates()
  }

  async submitUserMessage(params: { text: string; reply?: string; attachments?: Attachment[] }) {
    await this.#messenger?.commands.submitUserMessage(params)
  }

  async setComposerMessage(params: { text: string; reply?: string; attachments?: Attachment[] }) {
    await this.#messenger?.commands.setComposerMessage(params)
  }

  async setThreadId(threadId: string | null) {
    await this.#messenger?.commands.setThreadId({ threadId })
  }
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export interface ChatKitElement extends HTMLElement {
  addEventListener<K extends keyof ChatKitElementEventMap>(
    type: K,
    listener: (this: ChatKitElement, ev: ChatKitElementEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions,
  ): void

  removeEventListener<K extends keyof ChatKitElementEventMap>(
    type: K,
    listener: (this: ChatKitElement, ev: ChatKitElementEventMap[K]) => void,
    options?: boolean | EventListenerOptions,
  ): void
}

export function registerChatKitElement(tag = "openai-chatkit") {
  // SSR-safe guard
  if (!("customElements" in globalThis)) return

  if (!customElements.get(tag)) {
    customElements.define(tag, ChatKitElement)
  }
}
